<script setup lang="ts">
// todo:ai-review
import { formatTitle } from "@augmented/hierarchy/utils";
import type { ContentNode, RenderedContent, SQLiteExampleNode } from "#layers/books/types";

const props = defineProps<{
  content: RenderedContent | null | undefined;
  bookId: string;
  bookTitle: string;
  currentPath: string;
  currentNode?: ContentNode;
  /** Whether the book (at root level) has its own content */
  bookHasOwnContent?: boolean;
  /** Children of the current container node (or book root) */
  children?: ContentNode[];
}>();

const title = computed(() => {
  if (props.content?.title) return props.content.title;
  if (props.currentNode?.title) return props.currentNode.title;
  if (!props.currentPath) return props.bookTitle;

  // Derive title from path
  const lastPart = props.currentPath.split("/").pop() || "";
  return formatTitle(lastPart);
});

// Check if this is an SQLite example node
const isSQLiteExample = computed(() => {
  return props.currentNode?.sType === "sqlite-example";
});

// Cast to SQLiteExampleNode when applicable
const sqliteExampleNode = computed(() => {
  if (isSQLiteExample.value) {
    return props.currentNode as SQLiteExampleNode;
  }
  return undefined;
});

// Check if this is a container without its own content
// This includes book root (no currentPath, no currentNode) when book doesn't have own content
const isContainerWithoutContent = computed(() => {
  // SQLite examples are handled separately
  if (isSQLiteExample.value) return false;

  // Book root without content
  if (!props.currentPath && props.bookHasOwnContent === false) {
    return true;
  }
  // Nested container without content
  return (
    props.currentNode?.type === "container" &&
    props.currentNode?.hasOwnContent === false
  );
});
</script>

<template>
  <div class="h-full overflow-y-auto">
    <div class="max-w-4xl mx-auto px-6 py-8">
      <!-- Breadcrumbs -->
      <BookBreadcrumbs
        :book-id="bookId"
        :book-title="bookTitle"
        :current-path="currentPath"
      />

      <!-- SQLite Example - special rendering -->
      <BookSQLiteExample
        v-if="isSQLiteExample && sqliteExampleNode"
        :node="sqliteExampleNode"
        :book-id="bookId"
      />

      <!-- Standard content rendering -->
      <template v-else>
        <!-- Content title -->
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-6">
          {{ title }}
        </h1>

        <!-- Markdown content (rendered as HTML) TODO -->
        <div
          v-if="content?.html"
          class="prose prose-gray dark:prose-invert max-w-none"
          v-html="content.html"
        />

        <!-- Container without own content - show placeholder message -->
        <div
          v-else-if="isContainerWithoutContent && (!children || children.length === 0)"
          class="text-center py-12"
        >
          <UIcon
            name="i-heroicons-folder-open"
            class="w-16 h-16 mx-auto text-gray-300"
          />
          <p class="mt-4 text-gray-500">
            This section contains multiple topics. Select one from the navigation
            to continue.
          </p>
        </div>

        <!-- No content fallback (for unit nodes without content) -->
        <div
          v-else-if="!content?.html && !isContainerWithoutContent"
          class="text-center py-12"
        >
          <UIcon
            name="i-heroicons-document-text"
            class="w-16 h-16 mx-auto text-gray-300"
          />
          <p class="mt-4 text-gray-500">No content available for this section.</p>
        </div>

        <!-- Children list - shown at bottom for containers -->
        <BookChildrenList
          v-if="children && children.length > 0"
          :children="children"
          :title="content?.html ? 'In this section' : undefined"
        />
      </template>
    </div>
  </div>
</template>

<style>
/* ============================================
   Wiki Content Styles - Scoped under .prose
   Consistent with Tailwind Typography plugin
   ============================================ */

/* --- Headings --- */
.prose h1 {
  font-size: 2.25rem;
  font-weight: 800;
  line-height: 1.1;
  margin-top: 0;
  margin-bottom: 1rem;
  color: var(--color-gray-900);
}

.prose h2 {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.25;
  margin-top: 2rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--color-gray-200);
  color: var(--color-gray-900);
}

.prose h3 {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.375;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  color: var(--color-gray-900);
}

.prose h4 {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.4;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--color-gray-900);
}

.prose h5 {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  color: var(--color-gray-700);
}

.prose h6 {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  color: var(--color-gray-600);
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.dark .prose h1,
.dark .prose h2,
.dark .prose h3,
.dark .prose h4 {
  color: var(--color-gray-100);
}

.dark .prose h2 {
  border-color: var(--color-gray-700);
}

.dark .prose h5 {
  color: var(--color-gray-300);
}

.dark .prose h6 {
  color: var(--color-gray-400);
}

/* --- Paragraphs & Text --- */
.prose p {
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
  line-height: 1.75;
}

.prose p:first-child {
  margin-top: 0;
}

.prose strong {
  font-weight: 600;
  color: var(--color-gray-900);
}

.dark .prose strong {
  color: var(--color-gray-100);
}

.prose em {
  font-style: italic;
}

.prose small {
  font-size: 0.875rem;
}

/* --- Links --- */
.prose a {
  color: var(--color-primary-600);
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.15s ease;
}

.prose a:hover {
  color: var(--color-primary-700);
}

.dark .prose a {
  color: var(--color-primary-400);
}

.dark .prose a:hover {
  color: var(--color-primary-300);
}

/* --- Lists --- */
.prose ul {
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
  margin-left: 1.5rem;
  list-style-type: disc;
}

.prose ol {
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
  margin-left: 1.5rem;
  list-style-type: decimal;
}

.prose li {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  padding-left: 0.375rem;
}

.prose li::marker {
  color: var(--color-gray-400);
}

.dark .prose li::marker {
  color: var(--color-gray-500);
}

.prose ul ul,
.prose ul ol,
.prose ol ul,
.prose ol ol {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

/* --- Blockquotes --- */
.prose blockquote {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  padding-left: 1rem;
  border-left: 4px solid var(--color-gray-300);
  color: var(--color-gray-700);
  font-style: italic;
}

.dark .prose blockquote {
  border-color: var(--color-gray-600);
  color: var(--color-gray-300);
}

.prose blockquote p:first-child {
  margin-top: 0;
}

.prose blockquote p:last-child {
  margin-bottom: 0;
}

/* --- Code --- */
.prose code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.875em;
  font-weight: 500;
  color: var(--color-gray-800);
  background-color: var(--color-gray-100);
  padding: 0.2em 0.4em;
  border-radius: 0.25rem;
}

.dark .prose code {
  color: var(--color-gray-200);
  background-color: var(--color-gray-800);
}

.prose pre {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background-color: var(--color-gray-900);
  color: var(--color-gray-100);
  border-radius: 0.5rem;
  overflow-x: auto;
  font-size: 0.875rem;
  line-height: 1.7;
}

.prose pre code {
  background-color: transparent;
  padding: 0;
  color: inherit;
  font-size: inherit;
  font-weight: 400;
}

.dark .prose pre {
  background-color: var(--color-gray-950);
}

/* --- Horizontal Rules --- */
.prose hr {
  margin-top: 2rem;
  margin-bottom: 2rem;
  border: 0;
  border-top: 1px solid var(--color-gray-200);
}

.dark .prose hr {
  border-color: var(--color-gray-700);
}

/* --- Tables --- */
.prose table {
  width: 100%;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.prose thead {
  border-bottom: 2px solid var(--color-gray-300);
}

.dark .prose thead {
  border-color: var(--color-gray-600);
}

.prose th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--color-gray-900);
}

.dark .prose th {
  color: var(--color-gray-100);
}

.prose td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--color-gray-200);
}

.dark .prose td {
  border-color: var(--color-gray-700);
}

.prose tbody tr:last-child td {
  border-bottom: none;
}

/* --- Images & Figures --- */
.prose img {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 0.5rem;
  max-width: 100%;
  height: auto;
}

.prose figure {
  margin-top: 2rem;
  margin-bottom: 2rem;
}

.prose figcaption {
  margin-top: 0.75rem;
  font-size: 0.875rem;
  color: var(--color-gray-500);
  text-align: center;
}

.dark .prose figcaption {
  color: var(--color-gray-400);
}

/* --- Definition Lists --- */
.prose dl {
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
}

.prose dt {
  font-weight: 600;
  color: var(--color-gray-900);
  margin-top: 1rem;
}

.dark .prose dt {
  color: var(--color-gray-100);
}

.prose dd {
  margin-left: 1.5rem;
  margin-top: 0.25rem;
  color: var(--color-gray-700);
}

.dark .prose dd {
  color: var(--color-gray-300);
}

/* --- Abbreviations --- */
.prose abbr[title] {
  text-decoration: underline dotted;
  cursor: help;
}

/* --- Keyboard Input --- */
.prose kbd {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.875em;
  padding: 0.125rem 0.375rem;
  background-color: var(--color-gray-100);
  border: 1px solid var(--color-gray-300);
  border-radius: 0.25rem;
  box-shadow: 0 1px 0 var(--color-gray-300);
}

.dark .prose kbd {
  background-color: var(--color-gray-800);
  border-color: var(--color-gray-600);
  box-shadow: 0 1px 0 var(--color-gray-600);
}

/* --- Mark/Highlight --- */
.prose mark {
  background-color: var(--color-yellow-200);
  padding: 0.125rem 0.25rem;
  border-radius: 0.125rem;
}

.dark .prose mark {
  background-color: var(--color-yellow-800);
  color: var(--color-yellow-100);
}

/* --- Subscript/Superscript --- */
.prose sub,
.prose sup {
  font-size: 0.75em;
  line-height: 0;
  position: relative;
  vertical-align: baseline;
}

.prose sup {
  top: -0.5em;
}

.prose sub {
  bottom: -0.25em;
}

/* ============================================
   Wiki-Specific Styles
   ============================================ */

/* Wiki links - internal links between pages */
.prose .wiki-link {
  color: var(--color-primary-600);
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
  transition: color 0.15s ease, text-decoration-style 0.15s ease;
}

.prose .wiki-link:hover {
  color: var(--color-primary-700);
  text-decoration-style: solid;
}

.dark .prose .wiki-link {
  color: var(--color-primary-400);
}

.dark .prose .wiki-link:hover {
  color: var(--color-primary-300);
}

/* Broken wiki links - links to non-existent pages */
.prose .wiki-link-broken {
  color: var(--color-red-600);
  text-decoration: underline;
  text-decoration-style: wavy;
  text-underline-offset: 2px;
  cursor: not-allowed;
}

.dark .prose .wiki-link-broken {
  color: var(--color-red-400);
}

/* Wiki embeds - transcluded content from other pages */
.prose .wiki-embed {
  padding: 1rem 1.25rem;
  margin: 1.5rem 0;
  background-color: var(--color-gray-50);
  border-left: 4px solid var(--color-primary-500);
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.dark .prose .wiki-embed {
  background-color: var(--color-gray-800);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.2);
}

.prose .wiki-embed-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-gray-500);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.dark .prose .wiki-embed-title {
  color: var(--color-gray-400);
}

/* Wiki tags - inline tags/labels */
.prose .wiki-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-primary-700);
  background-color: var(--color-primary-50);
  border-radius: 9999px;
  text-decoration: none;
}

.prose .wiki-tag:hover {
  background-color: var(--color-primary-100);
}

.dark .prose .wiki-tag {
  color: var(--color-primary-300);
  background-color: var(--color-primary-900);
}

.dark .prose .wiki-tag:hover {
  background-color: var(--color-primary-800);
}

/* Wiki callouts/admonitions */
.prose .wiki-callout {
  padding: 1rem 1.25rem;
  margin: 1.5rem 0;
  border-radius: 0.5rem;
  border-left: 4px solid;
}

.prose .wiki-callout-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.prose .wiki-callout-note {
  background-color: var(--color-blue-50);
  border-color: var(--color-blue-500);
}

.prose .wiki-callout-note .wiki-callout-title {
  color: var(--color-blue-700);
}

.dark .prose .wiki-callout-note {
  background-color: var(--color-blue-950);
}

.dark .prose .wiki-callout-note .wiki-callout-title {
  color: var(--color-blue-300);
}

.prose .wiki-callout-warning {
  background-color: var(--color-amber-50);
  border-color: var(--color-amber-500);
}

.prose .wiki-callout-warning .wiki-callout-title {
  color: var(--color-amber-700);
}

.dark .prose .wiki-callout-warning {
  background-color: var(--color-amber-950);
}

.dark .prose .wiki-callout-warning .wiki-callout-title {
  color: var(--color-amber-300);
}

.prose .wiki-callout-tip {
  background-color: var(--color-green-50);
  border-color: var(--color-green-500);
}

.prose .wiki-callout-tip .wiki-callout-title {
  color: var(--color-green-700);
}

.dark .prose .wiki-callout-tip {
  background-color: var(--color-green-950);
}

.dark .prose .wiki-callout-tip .wiki-callout-title {
  color: var(--color-green-300);
}

.prose .wiki-callout-danger {
  background-color: var(--color-red-50);
  border-color: var(--color-red-500);
}

.prose .wiki-callout-danger .wiki-callout-title {
  color: var(--color-red-700);
}

.dark .prose .wiki-callout-danger {
  background-color: var(--color-red-950);
}

.dark .prose .wiki-callout-danger .wiki-callout-title {
  color: var(--color-red-300);
}

/* Wiki highlights - marked/highlighted text */
.prose .wiki-highlight {
  background-color: var(--color-yellow-200);
  padding: 0.125rem 0.25rem;
  border-radius: 0.125rem;
}

.dark .prose .wiki-highlight {
  background-color: var(--color-yellow-800);
  color: var(--color-yellow-100);
}

/* Wiki footnotes */
.prose .wiki-footnote-ref {
  font-size: 0.75rem;
  vertical-align: super;
  color: var(--color-primary-600);
  text-decoration: none;
  font-weight: 500;
}

.prose .wiki-footnote-ref:hover {
  text-decoration: underline;
}

.dark .prose .wiki-footnote-ref {
  color: var(--color-primary-400);
}

.prose .wiki-footnotes {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-gray-200);
  font-size: 0.875rem;
  color: var(--color-gray-600);
}

.dark .prose .wiki-footnotes {
  border-color: var(--color-gray-700);
  color: var(--color-gray-400);
}
</style>
